import {SerializedError} from "@reduxjs/toolkit";
import {FetchBaseQueryError, skipToken} from "@reduxjs/toolkit/dist/query";
import {PermissionLevel, UserStatus} from "./model/user";
import {ShippingRecipient} from "./model/shipment";
import {AdminUser} from "./model/adminUser/adminUser";
import {CompanyCardDetail, CompanyCardField} from "./model/organization/companyCard";
import {StageField, StageIdentifier} from "./model/stage";
import {Nullable} from "../../types";
import {DeviceJobType} from "./model/deviceJob";
import {DeviceJobField} from "./model/device";
import {DriverCardField} from "./model/driverCard";
import {RoadsoftSubscription} from "./model/roadsoft";

export enum HardwareType {
    DSX = "DSX",
    DSX3 = "DSX3",
}

// ------------------------------------
// Query type
// ------------------------------------

export type BaseModelType = {
    [key: string]: any;
};

export type ListResponseType = string | BaseModelType;

export type ListQuery<T extends ListResponseType, V extends ListRequest> = (
    request: V | typeof skipToken,
    options?: {pollingInterval?: number}
) => {
    data?: ListResponse<T>;
    isLoading: boolean;
    error?: FetchBaseQueryError | SerializedError;
};

// ------------------------------------
// Default Request/Response
// ------------------------------------

export interface StandardResponse {
    Code: string;
    Message: string;
}

export interface Pagination {
    total?: number;
    pageSize: number;
}

export interface ListResponse<T extends string | BaseModelType> {
    pagination: Pagination;
    payload: T[];
}

export interface ListRequestParams {
    offset?: number;
    pageSize?: number;
    sort?: string;
    filter?: string;
    showTotal?: boolean;
    pollingInterval?: number;
    [key: string]: string | boolean | number | undefined;
}

export interface ListRequest {
    params?: ListRequestParams;
}

export interface IdListRequest extends ListRequest {
    id: number;
}

export interface OrganizationIdListRequest extends IdListRequest {
    organizationId: number;
}

// ------------------------------------
// Session Request/Response
// ------------------------------------

export interface LoginRequest {
    email: string;
    password: string;
    timezone: string;
}

export interface SessionResponse {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    permissionLevel: PermissionLevel;
}

// ------------------------------------
// Device Request/Response
// ------------------------------------

export interface DeviceFirmwareUpdateRequest {
    deviceId: number;
    firmwareId: number;
    scheduledAt: string;
}

export interface DeviceRestartFirmwareUpdateRequest {
    deviceIds: number[];
}

export interface DeviceUpdateStagesRequest {
    deviceId: number;
    primaryStage?: string;
    stages: string[];
}

export interface DevicesCommandRequest {
    deviceIds: Array<number>;
    command: string;
}

export interface DeviceRdlAuthenticateRequest {
    deviceId: number;
}

export enum DeviceRdlAuthenticationStatus {
    AUTHENTICATED = "authenticated",
    NOT_AUTHENTICATED = "not_authenticated",
    IN_PROGRESS = "in_progress",
    RDL_NOT_ACTIVE = "rdl_not_active",
}

export interface DeviceRdlAuthenticationStatusResponse {
    status: DeviceRdlAuthenticationStatus;
}

export interface DeviceMassStorageDownloadRequest {
    [DeviceJobField.DEVICE_ID]: number;
    [DeviceJobField.TYPE]: DeviceJobType;
    [DeviceJobField.STAGE]: string;
    [DeviceJobField.PERIOD_START]: string;
    [DeviceJobField.PERIOD_END]: string;
}

export interface DeviceDriverCardDownloadRequest {
    [DeviceJobField.DEVICE_ID]: number;
    [DeviceJobField.TYPE]: DeviceJobType;
    [DeviceJobField.STAGE]: string;
    [DeviceJobField.DRIVER_CARD_NUMBER]: string;
    [DeviceJobField.DRIVER_CARD_SLOT]: number;
}

export interface ActiveDriverCardsResponse {
    [DriverCardField.SLOT]: string;
    [DriverCardField.CARD_ID]: string;
}

// ------------------------------------
// Order Request/Response
// ------------------------------------

export interface CancelRequest {
    id: number;
    cancelReason?: string;
}

export interface OrderUpdateAdminUserRequest {
    id: number;
    adminUser: Partial<AdminUser>;
}

export interface OrderUpdateRecipientRequest {
    id?: number;
    shippingRecipient: ShippingRecipient;
}

export interface OrderUpdateCommentRequest {
    id: number;
    comment: string;
}

// ------------------------------------
// Customer Request/Response
// ------------------------------------

export interface DkvCustomerNameResolveRequest {
    id: number;
}

export interface DkvCustomerNameResolveResponse {
    id: number;
}

export interface DkvUpdateCustomerNameRequest {
    name: string;
    dkvCustomerNumber: number;
}

// ------------------------------------
// User Request/Response
// ------------------------------------

export interface UserUpdateRequest {
    id: number;
    password: string;
    email: string;
    firstName: string;
    lastName: string;
    status: UserStatus;
    permissionLevel: PermissionLevel;
}

export interface UserCreateRequest {
    password: string;
    email: string;
    firstName: string;
    lastName: string;
    status: UserStatus;
    permissionLevel: PermissionLevel;
}

export interface CreateResponse {
    id: number;
}

export interface UpdatePasswordRequest {
    oldPassword: string;
    newPassword: string;
}

export enum ApiTag {
    SESSION = "SESSION",
    USER = "USER",
    DEVICE = "DEVICE",
    DEVICE_DRIVER_CARDS = "DEVICE_DRIVER_CARDS",
    HARDWARE_TYPE = "HARDWARE_TYPE",
    FIRMWARE = "FIRMWARE",
    DEFAULT_FIRMWARE = "DEFAULT_FIRMWARE",
    FIRMWARE_STATE = "FIRMWARE_STATE",
    FIRMWARE_TARGET = "FIRMWARE_TARGET",
    ORDER = "ORDER",
    ORDER_STATUS = "ORDER_STATUS",
    TERMINATION = "TERMINATION",
    TERMINATION_ITEM = "TERMINATION_ITEM",
    TERMINATION_COMMUNICATION = "TERMINATION_COMMUNICATION",
    STAGE = "STAGE",
    ORGANIZATION = "ORGANIZATION",
    ADMIN_USER = "ADMIN_USER",
    ORGANIZATION_DKV_CUSTOMER_NUMBERS = "ORGANIZATION_DKV_CUSTOMER_NUMBERS",
    ORGANIZATION_DEVICE_JOB = "ORGANIZATION_DEVICE_JOB",
    SHIPMENT = "SHIPMENT",
    DEVICE_HISTORY = "DEVICE_HISTORY",
    EETS_DEVICE_HISTORY = "EETS_DEVICE_HISTORY",
    SHIPMENT_STATUS = "SHIPMENT_STATUS",
    COMPANY_CARD = "COMPANY_CARD",
    EETS_DEVICE = "EETS_DEVICE",
    SMS_COMMANDS = "SMS_COMMANDS",
    CARD_SERVER = "CARD_SERVER",
    DEVICE_RDL_STATUS = "DEVICE_RDL_STATUS",
    ASSET = "ASSET",
    ROADSOFT = "ROADSOFT",
}

// ------------------------------------
// Organization Request/Response
// ------------------------------------

export interface OrganizationUpdateRequest {
    id: number;
    name: string;
    country: string;
    locked: boolean;
    lockedReason: string;
    productiveStage: StageIdentifier;
    mainDKVCustomerNumber: string;
}

export interface OrganizationCreateRequest {
    name: string;
    country: string;
    adminEmail: string;
    adminLanguage: string;
    mainDKVCustomerNumber?: string;
}

export interface OrganizationCreateFromOrderRequest {
    orderId: number;
    stageIdentifier: string;
}

export interface OrganizationCreateFromOrderResponse {
    id: number;
}

export interface OrganizationDKVCustomerNumberCreateRequest {
    organizationId: number;
    name: string;
    dkvCustomerNumber: string;
}

export interface OrganizationAddStageRequest {
    organizationId: number;
    stage: string;
    adminEmail: string;
}

export interface OrganizationDevicesRequest {
    organizationId: number;
    deviceIds: number[];
    comment?: string;
}

// ------------------------------------
// Company Card Request/Response
// ------------------------------------

export interface CompanyCardListRequest {
    [CompanyCardField.ORGANIZATION_ID]: number;
}

export interface CompanyCardByIdRequest extends CompanyCardListRequest {
    [CompanyCardField.ID]?: number;
}

export interface EetsDeviceByIdRequest {
    organizationId: number;
    id: number;
}

export type CompanyCardCreateRequest = CompanyCardByIdRequest &
    Pick<
        CompanyCardDetail,
        | CompanyCardField.CARD_ID
        | CompanyCardField.CARD_READER_SLOT
        | CompanyCardField.VALID_UNTIL
        | CompanyCardField.COMMENT
    >;

export interface ErrorMessage {
    body: string;
    field: string;
    severity: string;
    title: string;
    params: Map<string, string>;
}

// ------------------------------------
// Roadsoft Config Request/Response
// ------------------------------------

export interface RoadsoftConfigRequest {
    organizationId: number;
    organizationCountry: string;
    subscription: RoadsoftSubscription;
}

// ------------------------------------
// Update Stage Default Firmware Request/Response
// ------------------------------------

export interface UpdateStageDefaultFirmwareRequest {
    [StageField.IDENTIFIER]: StageIdentifier;
    [StageField.DEFAULT_FIRMWARE_ID]: Nullable<number>;
}
